/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { XMLParser } from 'fast-xml-parser'; // Correctly import XMLParser
import { Store } from '@ngrx/store';
import { AppState } from '@app/Store/reducers';
import { iEdmx } from '@app/Shared/interfaces/iEntity';
import { parseEdmxData, parseTruemetadata } from '@app/Shared/util/parse';
import { environment } from '@environments/environment';
import { iButtonItem } from '@app/Shared/interfaces/iMenu';
import { AddParentChildIn } from '@app/Shared/models/add-parent-in.model';
import { RemoveParentChildIn } from '@app/Shared/models/remove-parent-in.model';
import { ChangeParentChildLinkKey } from '@app/Shared/models/change-parent-child-link-key.model';
import { ReassignMatch } from '@app/Shared/models/reassign-match.model';
import { OverrideHistoryIn } from '@app/Shared/models/override-history-in.model';
import { ReturnSurvivorshipIn } from '@app/Shared/models/return-survivorship-in.model';
import { pluralize } from '@app/Shared/util/stringManipulation';

const { oData: baseUrl, api: apiUrl, metadata: metadataUrl } = environment.baseUrls;
const TRUEMETADATA_URL = `${metadataUrl}`;

@Injectable({
    providedIn: 'root',
})
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class OdataService {
    constructor(
        private http: HttpClient,
        private store: Store<AppState>,
    ) {}

    // getMetaData(query: string): Observable<any> {
    //     const url = `${baseUrl}${query}`;
    //     return this.http.get(url, { headers: { Accept: 'application/xml' }, responseType: 'text' }).pipe(
    //         map((xmlString: string) => {
    //             const edmx: iEdmx = this.parseXmlToJs(xmlString);
    //             return parseEdmxData(edmx);
    //         }),
    //         catchError((error) => {
    //             console.error('[ERROR]: FetchData', error);
    //             return throwError(() => new Error(`Error fetching XML data: ${error.message}`));
    //         }),
    //     );
    // }

    //https://api.cg-truedata-dev.com/api/truemetadata"

    getMetaData(query: string): Observable<any> {
        return this.http.get<any[]>(TRUEMETADATA_URL).pipe(
            map((data) => parseTruemetadata(data)),
            catchError((error) => {
                console.error('[OdataService] Truemetadata API error:', error?.message);
                return throwError(() => error);
            }),
        );
    }

    private parseXmlToJs(xmlString: string): iEdmx {
        try {
            const xmlParser = new XMLParser({
                ignoreAttributes: false,
                parseAttributeValue: true,
                attributeNamePrefix: '',
            });
            const parsed = xmlParser.parse(xmlString);

            // Use optional chaining to simplify the check
            if (parsed?.['edmx:Edmx']?.['edmx:DataServices']?.Schema) {
                return parsed;
            } else {
                throw new Error('Invalid XML format');
            }
        } catch (error) {
            throw new Error('Invalid XML format');
        }
    }

    addParentChild(model: AddParentChildIn): Observable<any> {
        const url: string = `${apiUrl}link/${model.masterTable}/${model.masterKey}/${model.linkTable}?relationship=${model.relationship}&newRelationKey=${model.newRelationKey}`;
        return this.http.post(url, null, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    changeParentChildLinkKey(model: ChangeParentChildLinkKey): Observable<any> {
        const url: string = `${apiUrl}link/${model.masterTable}/${model.masterKey}/${model.linkTable}/${model.linkKey}?newMasterKey=${model.newMasterKey}&relationship=${model.relationship}`;
        return this.http.put(url, null, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    removeParentChild(model: RemoveParentChildIn): Observable<any> {
        const url: string = `${apiUrl}link/${model.masterTable}/${model.masterKey}/${model.linkTable}/${model.linkKey}`;
        return this.http.delete(url, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    reassignMatch(model: ReassignMatch): Observable<any> {
        const url: string = `${apiUrl}match/${model.masterTable}/${model.masterKey}/${model.datasetName}/${model.matchKey}?newMasterKey=${model.newMasterKey}`;
        return this.http.put(url, null, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    returnSurvivorship(model: ReturnSurvivorshipIn): Observable<any> {
        const url: string = `${apiUrl}merge/${model.masterTable}/${model.masterKey}/${model.historyKey}`;
        return this.http.delete(url, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    overrideHistory(model: OverrideHistoryIn): Observable<any> {
        const url: string = `${apiUrl}merge/${model.masterTable}/${model.masterKey}/${model.historyKey}?newFieldValue=${model.newFieldValue}`;
        return this.http.patch(url, null, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    getIngestDataSets(): Observable<any> {
        const url = `${baseUrl}IngestDataSets?$expand=IngestFile`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataSets Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getIngestDataSetsPaged(top: number, skip: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}IngestDataSets?$expand=IngestFile&$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataSetsPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/IngestFiles
     */
    getIngestFiles(): Observable<any> {
        const url = `${baseUrl}IngestFiles`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get IngestFiles Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/IngestDataFields
     */
    getIngestDataFields(): Observable<any> {
        const url = `${baseUrl}IngestDataFields`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataFields Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getIngestDataFieldsPaged(top: number, skip: number, ingestDataSetKey: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}IngestDataFields?$filter=IngestDataSetKey eq ${ingestDataSetKey}&$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataFieldsPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/IngestStatMaps
     */
    getIngestStatMaps(): Observable<any> {
        const url = `${baseUrl}IngestStatMaps`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get IngestStatMaps Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getIngestStatMapsPaged(top: number, skip: number, ingestDataFieldKey: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}IngestStatMaps?$filter=IngestDataFieldKey eq ${ingestDataFieldKey}&$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestStatMapsPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/MasterDataFields
     */
    getMasterDataFields(): Observable<any> {
        const url = `${baseUrl}MasterDataFields`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get MasterDataFields Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getMasterDataFieldsPaged(top: number, skip: number, masterTableKey: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}MasterDataFields?$filter=MasterTableKey eq ${masterTableKey}&$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get MasterDataFieldsPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/MasterTables
     */
    getMasterTables(): Observable<any> {
        const url = `${baseUrl}MasterTables`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get MasterTables Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getMasterTablesPaged(top: number, skip: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}MasterTables?$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get MasterTablesPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getIngestRelationshipsPaged(top: number, skip: number, filter?: string, orderby?: string): Observable<{ value: any[]; count: number }> {
        const expand =
            'IngestDataField($expand=IngestDataSet($select=IngestDataSetNm,IngestDataSetKey,SrcTrukey);$select=IngestDataFieldNm,IngestDataFieldKey),' +
            'MasterDataField($expand=MasterTable($select=MasterTableNm,MasterTableOdataNm,MasterTableKey);$select=MasterDataFieldNm,MasterDataFieldKey,MasterDataFieldOdataNm),' +
            'MasterTable($select=MasterTableNm,MasterTableKey,MasterTableOdataNm,MasterTableType)';

        const queryParts = [`$expand=${expand}`, `$top=${top}`, `$skip=${skip}`, '$count=true'];
        if (orderby) {
            queryParts.push(`$orderby=${orderby}`);
        }
        if (filter) {
            queryParts.push(`$filter=${filter}`);
        }

        const url = `${baseUrl}IngestDataSetRltnshps?${queryParts.join('&')}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataSetRltnshps Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    createIngestRelationship(data: {
        IngestDataFieldKey: number;
        MasterDataFieldKey: number;
        BaseMasterTableKey: number;
        BaseMasterTableOdataNm: string;
        BaseMasterDataFieldKey: number;
        BaseMasterDataFieldOdataNm: string;
        IngestDataSetRltnshpType: string;
        [key: string]: any;
    }): Observable<any> {
        const url = `${baseUrl}IngestDataSetRltnshps`;
        return this.http.post<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestRelationship Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateIngestRelationship(
        key: number,
        data: {
            IngestDataFieldKey?: number;
            MasterDataFieldKey?: number;
            BaseMasterTableKey?: number;
            BaseMasterTableOdataNm?: string;
            BaseMasterDataFieldKey?: number;
            BaseMasterDataFieldOdataNm?: string;
            IngestDataSetRltnshpType?: string;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}IngestDataSetRltnshps(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestRelationship Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    deleteIngestRelationship(key: number): Observable<any> {
        const url = `${baseUrl}IngestDataSetRltnshps(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestRelationship Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getMasterTableRelationsPaged(top: number, skip: number, filter?: string, orderby?: string): Observable<{ value: any[]; count: number }> {
        const expand = 'MasterTable($select=MasterTableNm,MasterTableKey,MasterTableOdataNm)';
        const queryParts = [`$expand=${expand}`, `$top=${top}`, `$skip=${skip}`, '$count=true'];
        if (orderby) {
            queryParts.push(`$orderby=${orderby}`);
        }
        if (filter) {
            queryParts.push(`$filter=${filter}`);
        }

        const url = `${baseUrl}MasterTableRelations?${queryParts.join('&')}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get MasterTableRelations Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    createMasterTableRelation(data: {
        MasterTableKey: number;
        RelationType: string;
        MasterTableOdataNm: string;
        MasterKeyOdataNm: string;
        RelationTableOdataNm: string;
        RelationKeyOdataNm?: string;
        LinkTableOdataNm?: string;
        LinkKeyOdataNm?: string;
        [key: string]: any;
    }): Observable<any> {
        const url = `${baseUrl}MasterTableRelations`;
        return this.http.post<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create MasterTableRelation Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateMasterTableRelation(
        key: number,
        data: {
            MasterTableKey?: number;
            RelationType?: string;
            MasterTableOdataNm?: string;
            MasterKeyOdataNm?: string;
            RelationTableOdataNm?: string;
            RelationKeyOdataNm?: string;
            LinkTableOdataNm?: string;
            LinkKeyOdataNm?: string;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}MasterTableRelations(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update MasterTableRelation Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    deleteMasterTableRelation(key: number): Observable<any> {
        const url = `${baseUrl}MasterTableRelations(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete MasterTableRelation Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    createIngestDataSet(data: { IngestDataSetNm: string; IngestFileKey: number; SrcTrukey: number; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}IngestDataSets`;
        return this.http.post<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestDataSet Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /** IngestFiles CRUD */
    createIngestFile(
        data: {
            IngestFileNm: string;
            IngestFileProviderNm?: string;
            IngestFileFormatTxt?: string;
            IngestFileLocationDesc?: string;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        return this.postWithEntityKey('IngestFiles', 'IngestFileKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestFile Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateIngestFile(
        key: number,
        data: {
            IngestFileNm?: string;
            IngestFileProviderNm?: string;
            IngestFileFormatTxt?: string;
            IngestFileLocationDesc?: string;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}IngestFiles(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestFile Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    deleteIngestFile(key: number): Observable<any> {
        const url = `${baseUrl}IngestFiles(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestFile Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateIngestDataSet(key: number, data: { IngestDataSetNm?: string; IngestFileKey?: number; SrcTrukey?: number; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}IngestDataSets(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestDataSet Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    deleteIngestDataSet(key: number): Observable<any> {
        const url = `${baseUrl}IngestDataSets(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestDataSet Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    createIngestDataField(data: {
        IngestDataFieldNm: string;
        IngestDataSetKey: number;
        IngestDataFieldType?: string;
        IngestDataFieldNullableFlg?: boolean;
        [key: string]: any;
    }): Observable<any> {
        const url = `${baseUrl}IngestDataFields`;
        return this.http.post<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    updateIngestDataField(
        key: number,
        data: { IngestDataFieldNm?: string; IngestDataSetKey?: number; IngestDataFieldType?: string; IngestDataFieldNullableFlg?: boolean; [key: string]: any },
    ): Observable<any> {
        const url = `${baseUrl}IngestDataFields(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    deleteIngestDataField(key: number): Observable<any> {
        const url = `${baseUrl}IngestDataFields(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    createIngestStatMap(data: {
        IngestSrcStat: string;
        IngestStdStat: string;
        IngestMstrPrcssInd: string;
        IngestStatPriorityLvl: number;
        IngestDataFieldKey: number;
        [key: string]: any;
    }): Observable<any> {
        const url = `${baseUrl}IngestStatMaps`;
        return this.http.post<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestStatMap Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    updateIngestStatMap(
        key: number,
        data: { IngestSrcStat?: string; IngestStdStat?: string; IngestMstrPrcssInd?: string; IngestStatPriorityLvl?: number; IngestDataFieldKey?: number; [key: string]: any },
    ): Observable<any> {
        const url = `${baseUrl}IngestStatMaps(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestStatMap Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    deleteIngestStatMap(key: number): Observable<any> {
        const url = `${baseUrl}IngestStatMaps(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestStatMap Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    createMasterTable(data: {
        MasterTableNm: string;
        MasterTableOdataNm?: string;
        MasterTableUiLabelTxt?: string;
        MasterTableType?: string;
        MasterTableDomainNm?: string;
        [key: string]: any;
    }): Observable<any> {
        const url = `${baseUrl}MasterTables`;
        return this.http.post<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create MasterTable Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateMasterTable(
        key: number,
        data: {
            MasterTableNm?: string;
            MasterTableOdataNm?: string;
            MasterTableUiLabelTxt?: string;
            MasterTableType?: string;
            MasterTableDomainNm?: string;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}MasterTables(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update MasterTable Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    deleteMasterTable(key: number): Observable<any> {
        const url = `${baseUrl}MasterTables(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete MasterTable Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /** MasterDataFields CRUD */
    createMasterDataField(data: {
        MasterDataFieldNm: string;
        MasterTableKey: number;
        MasterDataFieldOdataNm?: string;
        MasterDataFieldUiLabelTxt?: string;
        MasterDataFieldType?: string;
        MasterDataFieldReadOnly?: boolean;
        [key: string]: any;
    }): Observable<any> {
        const url = `${baseUrl}MasterDataFields`;
        return this.http.post<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create MasterDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateMasterDataField(
        key: number,
        data: {
            MasterDataFieldNm?: string;
            MasterTableKey?: number;
            MasterDataFieldOdataNm?: string;
            MasterDataFieldUiLabelTxt?: string;
            MasterDataFieldType?: string;
            MasterDataFieldReadOnly?: boolean;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}MasterDataFields(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update MasterDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    deleteMasterDataField(key: number): Observable<any> {
        const url = `${baseUrl}MasterDataFields(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete MasterDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getOdata(odataQuery: string, params: any): Observable<any[]> {
        let url: string;
        if (params === null || params === undefined) {
            url = `${baseUrl}${odataQuery}`;
        } else {
            url = `${baseUrl}${odataQuery}?${params}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response),
            catchError((error) => {
                console.error('[ERROR]: Get OData Request Error:', error.message);
                throw error;
            }),
        );
    }

    // map((response: any) => (Object.prototype.hasOwnProperty.call(response, 'value') ? response.value : response)),

    getRelations(query: string): Observable<any> {
        const url = `${apiUrl}${query}`;
        return this.http.get(url).pipe(
            map((response: any) => response),
            catchError((err) => {
                console.error('[ERROR]: Get Relationship Request Error', err.message);
                throw err;
            }),
        );
    }

    getEntitySources(entityName: string, keyField: string, keyValue: number | string): Observable<{ value: any[] }> {
        const pluralEntity = pluralize(entityName);
        const expandNav = entityName.charAt(0).toUpperCase() + entityName.slice(1) + 'Srcs';
        const filterVal = typeof keyValue === 'string' && isNaN(Number(keyValue)) ? `'${keyValue}'` : String(keyValue);
        const url = `${baseUrl}${pluralEntity}?$filter=${encodeURIComponent(keyField + ' eq ' + filterVal)}&$expand=${expandNav}($expand=Src)`;
        return this.http.get<{ value: any[] }>(url).pipe(
            catchError((err) => {
                console.error('[ERROR]: Get Entity Sources Error', err.message);
                throw err;
            }),
        );
    }

    getButtonList(): Observable<iButtonItem[]> {
        return of([
            { title: 'email', page: 'email', icon: 'email' },
            { title: 'share', page: 'share', icon: 'share' },
            { title: 'print', page: 'print', icon: 'print' },
        ]);
    }

    getParentData(masterTable: string, masterKey: string): Observable<any> {
        return this.http.get(`${apiUrl}link/${masterTable}/${masterKey}?relationship=Parent`);
    }

    getChildData(masterTable: string, masterKey: string): Observable<any> {
        return this.http.get(`${apiUrl}link/${masterTable}/${masterKey}?relationship=Child`);
    }

    getMergeData(masterTable: string, masterKey: string): Observable<any> {
        return this.http.get(`${apiUrl}merge/${masterTable}/${masterKey}`);
    }

    getMatchData(masterTable: string, masterKey: string): Observable<any> {
        return this.http.get(`${apiUrl}match/${masterTable}/${masterKey}`);
    }

    updateEntityRowData(entityName: string, key: any, rowData: any): Observable<any> {
        const url = `${baseUrl}${entityName}(${key})`;
        console.log('ODataService.updateEntityRowData called with:', url, rowData);

        return this.http.patch(url, rowData).pipe(
            map((response: any) => {
                console.log('ODataService.updateEntityRowData response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Update Entity Row Data Error:', error);
                return throwError(() => new Error(`Error updating entity row data: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Get all sources from the Srcs endpoint
     * GET {{baseURL}}/OData/v1/Srcs
     */
    getSources(): Observable<any[]> {
        const url = `${baseUrl}Srcs`;
        console.log('ODataService.getSources called with:', url);

        return this.http.get<any>(url).pipe(
            map((response: any) => {
                console.log('ODataService.getSources response:', response);
                // OData typically returns data in a 'value' array
                return response.value || response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Get Sources Error:', error);
                return throwError(() => new Error(`Error fetching sources: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Create a new source
     * POST {{baseURL}}/OData/v1/Srcs
     * @param sourceData Object containing SrcNm and other source fields
     */
    createSource(sourceData: { SrcNm: string; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}Srcs`;
        console.log('ODataService.createSource called with:', url, sourceData);

        return this.http.post<any>(url, sourceData).pipe(
            map((response: any) => {
                console.log('ODataService.createSource response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Create Source Error:', error);
                return throwError(() => new Error(`Error creating source: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Update an existing source
     * PATCH {{baseURL}}/OData/v1/Srcs(key)
     * @param key Primary key of the source to update
     * @param sourceData Object containing fields to update (SrcNm, RcrdCrtUsrId, SrcTruid, SrcRcrdBusStatNm, etc.)
     */
    updateSource(key: number, sourceData: { SrcNm?: string; RcrdCrtUsrId?: string; SrcTruid?: number; SrcRcrdBusStatNm?: string; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}Srcs(${key})`;
        console.log('ODataService.updateSource called with:', url, sourceData);

        return this.http.patch<any>(url, sourceData).pipe(
            map((response: any) => {
                console.log('ODataService.updateSource response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Update Source Error:', error);
                return throwError(() => new Error(`Error updating source: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Get all rule types from the RuleTypes endpoint
     * GET {{baseURL}}/OData/v1/RuleTypes
     */
    getRuleTypes(): Observable<any[]> {
        const url = `${baseUrl}DataFieldRuleTypes`;
        console.log('ODataService.getRuleTypes called with:', url);

        return this.http.get<any>(url).pipe(
            map((response: any) => {
                console.log('ODataService.getRuleTypes response:', response);
                return response.value || response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Get Rule Types Error:', error);
                return throwError(() => new Error(`Error fetching rule types: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Create a new rule type
     * POST {{baseURL}}/OData/v1/RuleTypes
     * @param ruleTypeData Object containing rule type fields
     */
    createRuleType(ruleTypeData: { RuleType?: string; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}DataFieldRuleTypes`;
        console.log('ODataService.createRuleType called with:', url, ruleTypeData);

        return this.http.post<any>(url, ruleTypeData).pipe(
            map((response: any) => {
                console.log('ODataService.createRuleType response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Create Rule Type Error:', error);
                return throwError(() => new Error(`Error creating rule type: ${error.message || 'Unknown error'}`));
            }),
        );
    }
    /**
     * Update an existing rule type
     * PATCH {{baseURL}}/OData/v1/RuleTypes(key)
     * @param key Primary key of the rule type to update
     * @param ruleTypeData Object containing fields to update
     */
    updateRuleType(key: number, ruleTypeData: { RuleType?: string; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}DataFieldRuleTypes(${key})`;
        console.log('ODataService.updateRuleType called with:', url, ruleTypeData);

        return this.http.patch<any>(url, ruleTypeData).pipe(
            map((response: any) => {
                console.log('ODataService.updateRuleType response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Update Rule Type Error:', error);
                return throwError(() => new Error(`Error updating rule type: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Get all rules from the Rules endpoint
     * GET {{baseURL}}/OData/v1/Rules
     */
    getRules(): Observable<any[]> {
        const url = `${baseUrl}DataFieldRules`;
        console.log('ODataService.getRules called with:', url);

        return this.http.get<any>(url).pipe(
            map((response: any) => {
                console.log('ODataService.getRules response:', response);
                return response.value || response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Get Rules Error:', error);
                return throwError(() => new Error(`Error fetching rules: ${error.message || 'Unknown error'}`));
            }),
        );
    }
    /**
     * Create a new rule
     * POST {{baseURL}}/OData/v1/Rules
     * @param ruleData Object containing rule fields
     */
    createRule(ruleData: {
        RuleName?: string;
        RuleTypeKey?: number;
        RuleFunction?: string;
        RuleInitialParameter?: string;
        RuleSubsequentParameter?: string;
        [key: string]: any;
    }): Observable<any> {
        const url = `${baseUrl}DataFieldRules`;
        console.log('ODataService.createRule called with:', url, ruleData);

        return this.http.post<any>(url, ruleData).pipe(
            map((response: any) => {
                console.log('ODataService.createRule response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Create Rule Error:', error);
                return throwError(() => new Error(`Error creating rule: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Update an existing rule
     * PATCH {{baseURL}}/OData/v1/Rules(key)
     * @param key Primary key of the rule to update
     * @param ruleData Object containing fields to update
     */
    updateRule(
        key: number,
        ruleData: { RuleName?: string; RuleTypeKey?: number; RuleFunction?: string; RuleInitialParameter?: string; RuleSubsequentParameter?: string; [key: string]: any },
    ): Observable<any> {
        const url = `${baseUrl}DataFieldRules(${key})`;
        console.log('ODataService.updateRule called with:', url, ruleData);

        return this.http.patch<any>(url, ruleData).pipe(
            map((response: any) => {
                console.log('ODataService.updateRule response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Update Rule Error:', error);
                return throwError(() => new Error(`Error updating rule: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    getMaxEntityKey(entitySet: string, keyField: string): Observable<number> {
        const url = `${baseUrl}${entitySet}?$orderby=${keyField} desc&$top=1&$select=${keyField}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => {
                const rows = res?.value ?? [];
                return rows.length > 0 ? Number(rows[0][keyField] ?? 0) : 0;
            }),
            catchError((error) => {
                console.error(`[ERROR]: Get max ${keyField} for ${entitySet} Error:`, error.message);
                return throwError(() => error);
            }),
        );
    }

    private postWithEntityKey<T extends Record<string, any>>(
        entitySet: string,
        keyField: string,
        data: T,
        key?: number,
    ): Observable<any> {
        const assignKey = (nextKey: number) => {
            const url = `${baseUrl}${entitySet}`;
            return this.http.post<any>(url, { ...data, [keyField]: nextKey });
        };

        if (key != null) {
            return assignKey(key);
        }

        return this.getMaxEntityKey(entitySet, keyField).pipe(
            switchMap((maxKey) => assignKey(maxKey + 1)),
        );
    }
}
